import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CartoonTextField } from './CartoonForm.component';
import CartoonButton, { CartoonIconButton } from './CartoonButton.component';
import AvatarPicker from './AvatarPicker.component';

interface SetupModalProps {
  onSetup: (name: string, avatarType: 'bundled' | 'custom', avatar: string) => void;
  lobbyId: string;
  takenAvatars?: string[];
}

const SetupModal: React.FC<SetupModalProps> = ({ onSetup, lobbyId, takenAvatars = [] }) => {
  const [name, setName] = useState('');
  const [avatarSelection, setAvatarSelection] = useState<{
    type: 'bundled' | 'custom';
    avatar: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const canSubmit = name.trim().length > 0 && avatarSelection !== null;

  const handleSubmit = () => {
    if (!canSubmit || !avatarSelection) return;
    onSetup(name.trim(), avatarSelection.type, avatarSelection.avatar);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #FF6F61 30%, #6B5B95 90%)',
          border: '4px solid black',
          borderRadius: '20px',
          boxShadow: '0 8px 0 black, 0 12px 30px rgba(0,0,0,0.4)',
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: '"Comic Sans MS", cursive, sans-serif',
          fontWeight: 'bold',
          color: 'white',
          textAlign: 'center',
          fontSize: '1.6rem',
          textShadow: '2px 2px 0 rgba(0,0,0,0.3)',
          flexShrink: 0,
        }}
      >
        Join the Party! 🎡
      </DialogTitle>

      <DialogContent sx={{ overflowY: 'auto', flex: 1 }}>
        <Box display="flex" flexDirection="column" gap="20px">
          {/* Lobby info + share */}
          <Box
            sx={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '12px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '2px solid rgba(255,255,255,0.4)',
            }}
          >
            <Typography
              sx={{
                color: 'white',
                fontFamily: '"Comic Sans MS", cursive, sans-serif',
                fontWeight: 'bold',
                fontSize: '0.9rem',
              }}
            >
              Lobby: <strong>{lobbyId}</strong>
            </Typography>
            <CartoonIconButton onClick={handleCopyLink} title="Copy invite link">
              <ContentCopyIcon sx={{ fontSize: '1.1rem', color: 'white' }} />
            </CartoonIconButton>
          </Box>
          {copied && (
            <Typography
              variant="caption"
              sx={{ color: 'white', textAlign: 'center', fontStyle: 'italic' }}
            >
              Link copied to clipboard!
            </Typography>
          )}

          {/* Name field */}
          <CartoonTextField
            label="Your name"
            variant="outlined"
            value={name}
            onChange={e => setName(e.target.value.slice(0, 20))}
            fullWidth
            inputProps={{ maxLength: 20 }}
            sx={{ background: 'rgba(255,255,255,0.9)', borderRadius: '15px' }}
          />

          {/* Avatar picker */}
          <AvatarPicker value={avatarSelection} onChange={(type, avatar) => setAvatarSelection({ type, avatar })} takenAvatars={takenAvatars} />
        </Box>
      </DialogContent>

      {/* Submit — pinned outside scrollable content */}
      <Box sx={{ flexShrink: 0, padding: '12px 24px 8px' }}>
        <CartoonButton
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          fullWidth
          sx={{ fontSize: '1.1rem', height: '56px', background: '#ff4081' }}
        >
          Join the game!
        </CartoonButton>
      </Box>
    </Dialog>
  );
};

export default SetupModal;
